import {
  Button,
  CardContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { MainContext } from "../../controllers/main"
import EmbedSlice from "../../models/bodySlices/embedSlice"
import ImageSlice from "../../models/bodySlices/imageSlice"
import QuoteSlice from "../../models/bodySlices/quoteSlice"
import TextSlice from "../../models/bodySlices/textSlice"
import BlockEditor from "../blockEditor/blockEditor"
import { BodySliceType } from "../../services/config/enum"
import {
  deepCopy,
  isValidURL,
  stripHtml,
  testYtURL,
} from "../../services/utilities/utility"
import ImagePicker from "../global/imagePicker"
import { LeaguesContext } from "../../controllers/leagues"
import League from "../../models/league"

const LeagueTranslationsEdit = ({ itemIndex }: { itemIndex: number }) => {
  const { showPadding } = useContext(MainContext)
  const {
    currentLeague,
    setCurrentLeague,
    editMode,
    translationsErrors,
    setTranslationsErrors,
    copyDetailsFromDefault,
    copyBodyFromDefault,
  } = useContext(LeaguesContext)

  // current item
  const currentItem = currentLeague.document.items[itemIndex]

  // errors managment
  const [bodyError, setBodyError] = useState<boolean>(false) // error for body

  // update translations errors on errors change
  useEffect(() => {
    if (bodyError) {
      translationsErrors.find(
        (item) => item.lang === currentItem.lang
      ).hasErrors = true
    } else {
      translationsErrors.find(
        (item) => item.lang === currentItem.lang
      ).hasErrors = false
    }

    setTranslationsErrors([...translationsErrors])
  }, [bodyError])

  // update errors on currentItem change
  useEffect(() => {
    let errorInBody = false
    if (currentItem.body) {
      currentItem.body.forEach((bodyItem) => {
        if (
          (bodyItem.sliceType === BodySliceType.BodyTextSlice &&
            !stripHtml(bodyItem.text).length) ||
          (bodyItem.sliceType === BodySliceType.BodyQuoteSlice &&
            !bodyItem.quoteText.length) ||
          (bodyItem.sliceType === BodySliceType.BodyImageSlice &&
            (!bodyItem.imgUrl.length || !isValidURL(bodyItem.imgUrl))) ||
          (bodyItem.sliceType === BodySliceType.BodyEmbedSlice &&
            (!bodyItem.embedUrl.length || !testYtURL(bodyItem.embedUrl)))
        ) {
          errorInBody = true
        }
      })
    }
    if (errorInBody) {
      setBodyError(true)
    } else {
      setBodyError(false)
    }
  }, [currentItem])

  return (
    <CardContent
      style={{
        height:
          showPadding === "yes" ? "calc(100vh - 200px)" : "calc(100vh - 183px)",
        maxHeight:
          showPadding === "yes" ? "calc(100vh - 200px)" : "calc(100vh - 183px)",
        overflowY: "scroll",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" style={{ height: 28 }}>
          <Typography variant="h6" className="card-title">
            Details
          </Typography>
          <Stack
            alignItems="center"
            justifyContent="flex-end"
            style={{ width: "100%" }}
            direction="row"
            spacing={2}
          >
            <FormGroup style={{ marginRight: -16 }}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!editMode}
                    checked={currentItem.isDefault}
                    onChange={(e) => {
                      const currentLeagueCopy: League = deepCopy(currentLeague)
                      currentLeagueCopy.document.items[itemIndex].isDefault =
                        e.target.checked
                      if (e.target.checked) {
                        currentLeagueCopy.document.items.forEach(
                          (item, index) => {
                            if (index !== itemIndex) {
                              item.isDefault = false
                            }
                          }
                        )
                      }
                      setCurrentLeague(currentLeagueCopy)
                    }}
                  />
                }
                label="Default"
              />
            </FormGroup>
            <Divider orientation="vertical" flexItem />
            <Button
              size="small"
              variant="outlined"
              style={{ height: 26, fontSize: 12 }}
              onClick={() => {
                copyDetailsFromDefault(itemIndex)
              }}
              disabled={
                currentItem.isDefault ||
                !editMode ||
                !currentLeague.document.items.filter((item) => item.isDefault)
                  .length
              }
            >
              Copy from default
            </Button>
          </Stack>
        </Stack>
        <TextField
          fullWidth
          size="small"
          label="Title"
          value={currentItem.title}
          onChange={(e) => {
            const currentLeagueCopy: League = deepCopy(currentLeague)
            currentLeagueCopy.document.items[itemIndex].title = e.target.value
            setCurrentLeague(currentLeagueCopy)
          }}
          disabled={!editMode}
          onBlur={() => {
            if (currentLeague.document.items[itemIndex].title) {
              const currentLeagueCopy: League = deepCopy(currentLeague)
              currentLeagueCopy.document.items[itemIndex].title =
                currentLeagueCopy.document.items[itemIndex].title.trim()
              setCurrentLeague(currentLeagueCopy)
            }
          }}
        />
        <Stack direction="row" spacing={2} style={{ width: "100%" }}>
          <ImagePicker
            title="Logo"
            pickedImage={currentItem.logo}
            setPickedImage={(selectedImage: {
              url: string
              id: string
              description?: string
            }) => {
              const currentLeagueCopy: League = deepCopy(currentLeague)
              currentLeagueCopy.document.items[itemIndex].logo =
                selectedImage.url
              setCurrentLeague(currentLeagueCopy)
            }}
            filters={{ category: "thumbnail" }}
            disableCategoryFilter
            height={190}
            width="100%"
            placeholderHeight={120}
            placeholderWidth="35%"
            disabled={!editMode}
          />
        </Stack>
        <Stack spacing={2}>
          <Stack direction="row" style={{ height: 28 }}>
            <Typography variant="h6" className="card-title">
              Body
            </Typography>
            <Stack
              alignItems="center"
              justifyContent="flex-end"
              style={{ width: "100%" }}
              direction="row"
              spacing={2}
            >
              <Button
                size="small"
                variant="outlined"
                style={{ height: 26, fontSize: 12 }}
                onClick={() => {
                  copyBodyFromDefault(itemIndex)
                }}
                disabled={
                  currentItem.isDefault ||
                  !editMode ||
                  !currentLeague.document.items.filter((item) => item.isDefault)
                    .length
                }
              >
                Copy from default
              </Button>
            </Stack>
          </Stack>
          <BlockEditor
            body={currentItem.body ?? []}
            setBody={(
              body: (EmbedSlice | ImageSlice | QuoteSlice | TextSlice)[]
            ) => {
              const currentLeagueCopy: League = deepCopy(currentLeague)
              currentLeagueCopy.document.items[itemIndex].body = body
              setCurrentLeague(currentLeagueCopy)
            }}
            editMode={editMode}
          />
        </Stack>
      </Stack>
    </CardContent>
  )
}

export default LeagueTranslationsEdit
